import type {
  ConfigAppEndpoints,
  DomainEntityConfiguration,
  OperationType,
} from '../../types/configuration';
import { operations } from '../../constants';
import { operationId } from '../has-entity-operation/operation-id';

const isOperationType = (key: string): key is OperationType =>
  (operations as ReadonlyArray<string>).includes(key);

/**
 * Build the set of operation ids a CACT-BE domain entity has opted into at config-build time.
 * `list` and `get` are always implicit. `deepClone` is included whenever the entity declares
 * `clone`; the schema-aware "actually supports deepClone" check still happens at runtime via
 * `hasEntityOperation` (which has access to the loaded schema). The pre-merged set is a
 * superset that runtime gates can narrow.
 */
const getAllowedOperationIds = (
  entity: Pick<DomainEntityConfiguration, 'operations'>,
): Set<OperationType> => {
  const ids = new Set<OperationType>(['list', 'get']);
  entity.operations?.forEach((entry) => ids.add(operationId(entry)));
  if (ids.has('clone')) ids.add('deepClone');
  return ids;
};

/**
 * Filter the global `ConfigAppEndpoints` down to just the operations a given CACT-BE entity has
 * opted into. Used at tenant-config build time (see `buildEntityConfigs`) so each entity carries
 * its own runtime-ready endpoints map. Custom operations live on `DomainEntityConfiguration.
 * customOperations`, not on the endpoints map, so they're not part of this filter.
 */
export const filterEndpointsForDomainEntity = (
  configEntityEndpoints: ConfigAppEndpoints,
  entity: Pick<DomainEntityConfiguration, 'operations'>,
): ConfigAppEndpoints => {
  const allowedIds = getAllowedOperationIds(entity);
  const { list, get, ...optionalEndpoints } = configEntityEndpoints;
  return {
    list,
    get,
    ...Object.fromEntries(
      Object.entries(optionalEndpoints).filter(
        ([key]) => isOperationType(key) && allowedIds.has(key),
      ),
    ),
  };
};
